'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Hl, Lesson, M, NumberQuiz, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/neural-nets'

const BackpropGraph = dynamic(() => import('../_components/widgets/NNWidgets').then(m => m.BackpropGraph), { ssr: false })

export default function Backpropagation() {
  return (
    <Lesson>
      <Step>
        <H>Every calculation is a graph</H>
        <P>
          Write any calculation as a diagram: values flow left to right through little operations — multiply, add, square.
          This is a <strong>computational graph</strong>. Here’s <M>{'L = (a \\cdot b + c)^2'}</M>, broken into steps:{' '}
          <M>{'d = a \\cdot b'}</M>, then <M>{'e = d + c'}</M>, then <M>{'L = e^2'}</M>.
        </P>
        <P>
          Computing the values left to right is the <strong>forward pass</strong>. Now the question that matters for learning:
          how much does <Hl c="pink">L</Hl> change if we nudge each of the <Hl c="green">inputs</Hl>?
        </P>
      </Step>

      <Step>
        <H>Walk the blame backwards</H>
        <P>
          Start at the end: L changes 1-for-1 with itself, so its gradient is 1. Then step backwards, one node at a time. At each
          node, the chain rule says: <strong>gradient here = gradient from above × this node’s local slope</strong>.
        </P>
        <Figure n="7.4" wide caption="Step backward through the graph. Each node’s gradient is the upstream gradient times its local slope. Change a, b and c and step again.">
          <BackpropGraph />
        </Figure>
        <P>Notice the patterns — they’re the same in every network:</P>
        <ul className="gd-list">
          <li>
            An <strong>add</strong> node passes the gradient straight through to both inputs (local slope 1).
          </li>
          <li>
            A <strong>multiply</strong> node sends each input the gradient times the <em>other</em> input.
          </li>
          <li>
            A <strong>square</strong> node multiplies by <M>{'2e'}</M>; a <strong>tanh</strong> by <M>{'1 - \\tanh^2'}</M>;
            and so on — each operation only needs its own little derivative.
          </li>
        </ul>
      </Step>

      <Step gate={['nq-bp', 'q-bp']}>
        <H>Check your understanding</H>
        <NumberQuiz
          id="nq-bp"
          question={<>For <M>{'L = (a \\cdot b + c)^2'}</M> with <M>{'a = 2, b = -3, c = 10'}</M>: what is <M>{'\\partial L / \\partial a'}</M>?</>}
          answer={-24}
          explain={<>e = 2·(−3) + 10 = 4, so ∂L/∂e = 2e = 8. Through the add: ∂L/∂d = 8. Through the multiply: ∂L/∂a = 8 × b = 8 × (−3) = −24.</>}
          hint={<>Work backwards: ∂L/∂e = 2e. The add passes it to d unchanged. The multiply gives a the upstream gradient times b.</>}
        />
        <Quiz
          id="q-bp"
          question="Why go backwards instead of nudging each input forward one at a time?"
          options={[
            { label: 'One backward sweep gives the gradient for every input at once, reusing shared work.', correct: true },
            { label: 'Going forward gives the wrong answer.', why: 'Nudging each input works (that’s numerical differentiation) — it’s just one full pass per parameter.' },
            { label: 'Computers can only multiply backwards.', why: 'It’s about efficiency, not capability.' },
          ]}
          explain="With a billion parameters, nudging each one would take a billion forward passes. Backpropagation takes about one."
        />
      </Step>

      <Step gate="manual-backprop">
        <H>Backprop by hand</H>
        <Exercise
          id="manual-backprop"
          title="Gradients of a one-neuron model"
          {...EX.manualBackprop}
          hints={[
            <>
              Compute forward first: <C>pred</C>, then <C>err</C>. Then <C>dErr = 2 * err</C>.
            </>,
            <>
              <C>{'return [dErr * x, dErr]'}</C>
            </>,
          ]}
        />
        <Callout kind="why">
          Backpropagation (popularised in 1986) is what made deep learning possible. It is nothing more than the chain rule,
          applied in the clever order. Next lesson, you’ll write code that does it automatically for <em>any</em> calculation.
        </Callout>
      </Step>
    </Lesson>
  )
}
